import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { QuizQuestion, QuizAnswer, useSubmitQuizAttempt, useQuizAnswers } from '@/hooks/useEducation';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, XCircle, HelpCircle } from 'lucide-react';

interface QuizComponentProps {
  moduleId: string;
  questions: QuizQuestion[];
  onComplete: (passed: boolean, score: number) => void;
}

export const QuizComponent: React.FC<QuizComponentProps> = ({ 
  moduleId, 
  questions, 
  onComplete 
}) => {
  const { user } = useAuth();
  const submitQuizAttempt = useSubmitQuizAttempt();
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  
  // Fetch quiz answers only after submission
  const { data: quizAnswers } = useQuizAnswers(moduleId, attemptId);

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const hasAnsweredCurrent = answers[currentQuestion?.id];

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    if (isLastQuestion) {
      submitQuiz();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const submitQuiz = async () => {
    setIsSubmitting(true);
    
    // Submit initial attempt to database
    if (user) {
      try {
        const attempt = await submitQuizAttempt.mutateAsync({
          user_id: user.id,
          module_id: moduleId,
          score: 0, // Will be calculated by server
          total_questions: totalQuestions,
          passed: false, // Will be calculated by server
          attempt_number: 1
        });
        
        // Calculate score on server using secure function
        const { data: scoreData, error: scoreError } = await supabase.rpc('calculate_quiz_score', {
          attempt_uuid: attempt.id,
          user_answers: answers
        });
        
        if (scoreError) throw scoreError;
        
        setAttemptId(attempt.id);
        // Results will be shown once quiz answers are fetched
      } catch (error) {
        console.error('Error submitting quiz attempt:', error);
        setIsSubmitting(false);
        return;
      }
    }
    
    setIsSubmitting(false);
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Calculate results when quiz answers are available
  useEffect(() => {
    if (quizAnswers && quizAnswers.length > 0 && !showResults) {
      const questionResults: Record<string, boolean> = {};
      let correctCount = 0;
      
      questions.forEach(question => {
        const userAnswer = answers[question.id];
        const quizAnswer = quizAnswers.find(qa => qa.question_id === question.id);
        const isCorrect = userAnswer === quizAnswer?.correct_answer;
        questionResults[question.id] = isCorrect;
        if (isCorrect) correctCount++;
      });
      
      const score = Math.round((correctCount / totalQuestions) * 100);
      const passed = score >= 70;
      
      setResults(questionResults);
      setShowResults(true);
      onComplete(passed, score);
    }
  }, [quizAnswers, answers, questions, totalQuestions, showResults, onComplete]);

  if (showResults && quizAnswers) {
    const correctCount = Object.values(results).filter(Boolean).length;
    const score = Math.round((correctCount / totalQuestions) * 100);
    const passed = score >= 70;

    return (
      <div className="space-y-6">
        <Card className={`border-2 ${passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {passed ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600" />
              )}
              Quiz {passed ? 'Passed' : 'Failed'}
            </CardTitle>
            <CardDescription>
              You scored {correctCount} out of {totalQuestions} questions correct
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${getScoreColor(score)}`}>
              {score}%
            </div>
            <p className="text-muted-foreground mt-2">
              {passed 
                ? "Congratulations! You can now proceed to the next module." 
                : "You need 70% to pass. Review the material and try again."}
            </p>
          </CardContent>
        </Card>

        {/* Show question-by-question results */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Question Review</h3>
          {questions.map((question, index) => {
            const isCorrect = results[question.id];
            const userAnswer = answers[question.id];
            const quizAnswer = quizAnswers?.find(qa => qa.question_id === question.id);
            
            return (
              <Card key={question.id} className="border-l-4 border-l-muted">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    {isCorrect ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    Question {index + 1}
                  </CardTitle>
                  <CardDescription>{question.question_text}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">Your answer: </span>
                      <span className={isCorrect ? 'text-green-600' : 'text-red-600'}>
                        {userAnswer}
                      </span>
                    </div>
                    {!isCorrect && quizAnswer && (
                      <div>
                        <span className="font-medium">Correct answer: </span>
                        <span className="text-green-600">{quizAnswer.correct_answer}</span>
                      </div>
                    )}
                    {quizAnswer?.explanation && (
                      <Alert>
                        <HelpCircle className="h-4 w-4" />
                        <AlertDescription>{quizAnswer.explanation}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Quiz: Question {currentQuestionIndex + 1}</span>
            <span className="text-sm text-muted-foreground">
              {currentQuestionIndex + 1} of {totalQuestions}
            </span>
          </CardTitle>
          <CardDescription>{currentQuestion?.question_text}</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={answers[currentQuestion?.id] || ''}
            onValueChange={(value) => handleAnswerChange(currentQuestion.id, value)}
          >
            {currentQuestion?.options.map((option, index) => (
              <div key={index} className="flex items-center space-x-2 p-3 rounded-lg hover:bg-muted/50">
                <RadioGroupItem value={option} id={`option-${index}`} />
                <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        >
          Previous
        </Button>
        
        <Button
          onClick={handleNext}
          disabled={!hasAnsweredCurrent || isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : isLastQuestion ? 'Submit Quiz' : 'Next Question'}
        </Button>
      </div>
    </div>
  );
};